import api from "./agritech";

export type Field = {
  id: number;
  client_id: number;
  name?: string;
  farm?: string;
  field_type?: string;
  crop_type?: string;
  valve_group?: string;
  timezone: string;
  import_frequency: 5 | 15;
};
export async function getField(
  token: string,
  clientId: number,
  fieldId: number
) {
  const response = await api.get(`${clientId}/fields/${fieldId}`, {
    headers: { Authorization: "Bearer " + token },
  });
  return response.data;
}

type RootzoneProfileInput = {
  id: string;
  name: string;
  depths: number[] | null;
  active: boolean;
  archived: boolean;
  // Per-profile parameter values held locally on the form. Extracted into a
  // separate `profile_parameters` array at submit time so the backend can
  // create timestamped Parameter rows with rootzone_profile_id set.
  params?: {
    field_capacity?: number | string;
    refill_point?: number | string;
    irrigation_trigger?: number | string;
  } & Partial<Record<`smd_${number}`, number | string>>;
};

type FieldData = {
  sensors: { multiplier: number }[];
  rootzone_profiles?: RootzoneProfileInput[];
};

const PROFILE_PARAM_TYPES = {
  field_capacity: "field_capactiy",
  refill_point: "refill_point",
  irrigation_trigger: "irrigation_trigger",
  smd_5: "smd_5",
  smd_10: "smd_10",
  smd_15: "smd_15",
  smd_20: "smd_20",
  smd_25: "smd_25",
  smd_30: "smd_30",
  smd_35: "smd_35",
  smd_40: "smd_40",
  smd_45: "smd_45",
  smd_50: "smd_50",
  smd_55: "smd_55",
  smd_60: "smd_60",
  smd_65: "smd_65",
  smd_70: "smd_70",
  smd_75: "smd_75",
  smd_80: "smd_80",
  smd_85: "smd_85",
} as const;
const PROFILE_PARAM_FIELDS = Object.entries(PROFILE_PARAM_TYPES) as Array<
  [
    keyof typeof PROFILE_PARAM_TYPES,
    typeof PROFILE_PARAM_TYPES[keyof typeof PROFILE_PARAM_TYPES]
  ]
>;

// Inverse of PROFILE_PARAM_TYPES: API parameter-type slug -> the form field key
// bound under `params`. Only `field_capacity` differs from its slug (the API
// type is misspelled `field_capactiy`); everything else maps to itself.
const PROFILE_PARAM_FIELD_BY_SLUG = Object.fromEntries(
  PROFILE_PARAM_FIELDS.map(([field, type]) => [type, field])
) as Record<string, keyof typeof PROFILE_PARAM_TYPES>;

/**
 * Remap each rootzone profile's API `params` (keyed by parameter-type slug) to
 * the form field keys the editor binds to, so the per-profile FC/refill/trigger
 * /SMD inputs repopulate when editing an existing field. The single-field
 * endpoint nests these values; the clients-list payload does not. Unknown slugs
 * pass through untouched.
 */
export function hydrateProfileParams(
  rootzoneProfiles: any[] | undefined | null
): any[] | undefined | null {
  if (!Array.isArray(rootzoneProfiles)) return rootzoneProfiles;
  return rootzoneProfiles.map((profile) => {
    const apiParams = (profile && profile.params) || {};
    const params: Record<string, number> = {};
    for (const [slug, value] of Object.entries(apiParams)) {
      const key = PROFILE_PARAM_FIELD_BY_SLUG[slug] ?? slug;
      params[key] = value as number;
    }
    return { ...profile, params };
  });
}

function formatValues(
  values: FieldData,
  initialValues?: { rootzone_profiles?: FieldData["rootzone_profiles"] }
) {
  // Strip per-profile `params` from the rootzone_profiles payload and emit them
  // as a separate `profile_parameters` array. A numeric value is saved; a value
  // that was loaded and then blanked emits an explicit clear (value: null) so
  // the backend removes it. A param that was never set stays untouched — and
  // because clears are gated on the *initial* value, a failed param load (which
  // leaves the form blank) can never wipe stored parameters.
  const profileParameters: Array<{
    rootzone_profile_id: string | null;
    type: typeof PROFILE_PARAM_TYPES[keyof typeof PROFILE_PARAM_TYPES];
    value: number | null;
  }> = [];
  const isBlank = (v: unknown) => v === undefined || v === null || v === "";
  const initialParamsById = new Map<string, Record<string, unknown>>();
  (initialValues?.rootzone_profiles ?? []).forEach((profile) => {
    if (profile && profile.id != null) {
      initialParamsById.set(String(profile.id), profile.params ?? {});
    }
  });
  const rootzone_profiles = values.rootzone_profiles?.map((profile) => {
    const { params = {}, ...rest } = profile;
    const initialParams = initialParamsById.get(String(profile.id)) ?? {};
    const rootzone_profile_id = profile.id === "default" ? null : profile.id;
    for (const [field, type] of PROFILE_PARAM_FIELDS) {
      const raw = params[field];
      if (!isBlank(raw)) {
        const num = typeof raw === "number" ? raw : parseFloat(raw as string);
        if (!Number.isFinite(num)) continue;
        profileParameters.push({ rootzone_profile_id, type, value: num });
        continue;
      }
      // Blank now, but had a value when loaded -> explicit clear.
      if (!isBlank(initialParams[field])) {
        profileParameters.push({ rootzone_profile_id, type, value: null });
      }
    }
    return rest;
  });
  return {
    ...values,
    sensors:
      values.sensors && values.sensors.length
        ? values.sensors.map((sensor) => {
            const { multiplier, ...rest } = sensor;
            const config = multiplier ? JSON.stringify({ multiplier }) : null;
            return { ...rest, config };
          })
        : [],
    ...(rootzone_profiles !== undefined ? { rootzone_profiles } : {}),
    ...(profileParameters.length ? { profile_parameters: profileParameters } : {}),
  };
}

export async function saveField(
  token: string,
  clientId: number,
  values: FieldData
) {
  const response = await api.post(`${clientId}/fields`, formatValues(values), {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
}

export async function updateField(
  token: string,
  clientId: number,
  fieldId: number,
  values: FieldData,
  initialValues?: { rootzone_profiles?: FieldData["rootzone_profiles"] }
) {
  const response = await api.patch(
    `${clientId}/fields/${fieldId}`,
    formatValues(values, initialValues),
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

export async function deleteField(
  token: string,
  clientId: number,
  fieldId: number
) {
  const response = await api.delete(`${clientId}/fields/${fieldId}`, {
    headers: { Authorization: "Bearer " + token },
  });
  return response.data;
}

export async function deleteDataField(token: string, values: object) {
  const response = await api.post(
    `deleteFieldData`,
    { ...values },
    {
      headers: { Authorization: "Bearer " + token },
    }
  );
  return response.data;
}

export async function importHistoricData(token: string, values: any) {
  const response = await api.post(
    `sensorDataImports`,
    { ...values },
    {
      headers: { Authorization: "Bearer " + token },
    }
  );
  return response.data;
}

export async function refreshMildew(token: string, values: any) {
  const response = await api.post(
    `refreshMildewData`,
    { ...values },
    {
      headers: { Authorization: "Bearer " + token },
    }
  );
  return response.data;
}

export async function listDeletedFields(token: string, clientId: number) {
  const response = await api.get(`${clientId}/deleted-fields`, {
    headers: { Authorization: "Bearer " + token },
  });
  return response.data;
}

export async function restoreField(
  token: string,
  clientId: number,
  fieldId: number
) {
  const response = await api.post(
    `${clientId}/fields/restore/${fieldId}`,
    {},
    {
      headers: { Authorization: "Bearer " + token },
    }
  );
  return response.data;
}
